import { StartupScreen } from "@cliqsuite/ui";
import type { ReactNode } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import { businessesCollection, eq, subscriptionPaymentsCollection } from "@cliqsuite/db";
import { useState } from "react";
import { useAuth } from "../../lib/auth";
import { BillingTab } from "../billing/BillingTab";
import { AuthFrame, AuthHead } from "./AuthFrame";

const BLOCKED_STATUSES = new Set(["inactive", "expired", "suspended"]);

const STATUS_COPY: Record<string, { title: string; body: string }> = {
  inactive: {
    title: "Pick a plan to start",
    body: "Choose a plan and send your payment. The portal opens as soon as it's approved.",
  },
  expired: {
    title: "Your plan has ended",
    body: "Renew to open the portal again. Registers keep selling offline in the meantime.",
  },
  suspended: {
    title: "This business is suspended",
    body: "CliqSuite paused this business. Registers and kitchen screens are locked. Your data is safe.",
  },
};

/**
 * Nothing server-side ever flips 'active'/'trial' to 'expired' when
 * subscription_period_end passes — derive it here instead, so an ended
 * period actually locks the portal (and grace_period_ends_at, below,
 * actually has something to extend). Period/grace dates are inclusive.
 */
export function effectiveSubscriptionStatus(
  business: { subscription_status: string; subscription_period_end: string | null },
  today = new Date().toISOString().slice(0, 10),
): string {
  const ended = !!business.subscription_period_end && business.subscription_period_end < today;
  if (ended && (business.subscription_status === "active" || business.subscription_status === "trial")) return "expired";
  return business.subscription_status;
}

/**
 * `core.businesses.subscription_status` starts 'inactive' on self-service
 * signup and is meant to stay gated from the rest of the platform until an
 * admin approves a payment (see services/sync-api's /auth/owner-signup) —
 * but nothing here ever actually checked it, so an account with no active
 * subscription could use every feature exactly like a paying one. 'trial'
 * and 'active' pass straight through; anything else is blocked unless
 * grace_period_ends_at (admin-granted, see admin-portal's
 * EditSubscriptionModal) is set and still in the future. The blocked
 * screen embeds BillingTab directly, so there's still a real way out:
 * submit a payment without needing access to the rest of the app first.
 */
export function SubscriptionGate({ businessId, children }: { businessId: string; children: ReactNode }) {
  const { logout } = useAuth();
  const { data: businessRows, isReady } = useLiveQuery(
    (q) => q.from({ b: businessesCollection }).where(({ b }) => eq(b.id, businessId)),
    [businessId],
  );
  const business = businessRows?.[0];
  const { data: payments } = useLiveQuery(
    (q) => q.from({ pay: subscriptionPaymentsCollection }).where(({ pay }) => eq(pay.business_id, businessId)),
    [businessId],
  );
  // Never briefly expose the portal while sync is still resolving the
  // subscription row. New accounts begin as inactive, so rendering children
  // here caused a visible (and misleading) unlocked-portal flash.
  const [showBilling, setShowBilling] = useState(false);
  if (!isReady || !business) {
    return (
      <StartupScreen label="Loading your account" />
    );
  }

  const today = new Date().toISOString().slice(0, 10);
  const inGrace = !!business.grace_period_ends_at && business.grace_period_ends_at >= today;
  const status = effectiveSubscriptionStatus(business, today);
  const blocked = BLOCKED_STATUSES.has(status) && !inGrace;
  if (!blocked) return <>{children}</>;

  const copy = STATUS_COPY[status] ?? STATUS_COPY.inactive!;

  // Suspended gets a plain notice first; paying again opens the same billing screen.
  if (status === "suspended" && !showBilling) {
    // The admin's reason wins; without one, a rejected last payment is the likely cause.
    const last = [...(payments ?? [])].sort((a, b) => b.submitted_at.localeCompare(a.submitted_at))[0];
    const reason = business.suspension_reason?.trim()
      ? `Reason: ${business.suspension_reason.trim().replace(/\.$/, "")}. `
      : last?.status === "rejected"
        ? `Your last payment was rejected: ${last.reject_reason || "the proof couldn't be matched"}. `
        : "";
    return (
      <AuthFrame>
        <div className="o-auth__form">
          <AuthHead icon="block" title={`${business.name} is suspended`} sub={copy.body} />
          <div className="o-note" style={{ marginTop: 0 }}>{reason}Contact support or pay again to reopen.</div>
          <button type="button" className="o-auth__submit" onClick={() => setShowBilling(true)}>
            Pay again
            <span className="ms" aria-hidden="true">arrow_forward</span>
          </button>
          <a className="o-link o-auth__alt" href="mailto:support@cliqsuite.app">
            Contact support
          </a>
          <button type="button" className="o-link o-auth__alt" onClick={logout}>
            Sign out
          </button>
        </div>
      </AuthFrame>
    );
  }

  return (
    <div className="o-locked">
      <main>
        <div className="o-page" style={{ maxWidth: 1248 }}>
          <header className="o-head">
            <span className="o-auth__icon">
              <span className="ms" aria-hidden="true" style={{ fontVariationSettings: "'FILL' 1" }}>lock</span>
            </span>
            <div className="o-head__text">
              <h1>{copy.title}</h1>
              <div>{copy.body}</div>
            </div>
            <button type="button" className="o-link" onClick={logout}>
              Sign out
            </button>
          </header>
          <BillingTab />
        </div>
      </main>
    </div>
  );
}
